//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { PoolTypeEnum, PoolTypeToString, yupErc20WeiOptional, yupErc20WeiRequired, yupEthAddrRequired } from './Enums'
import { CTOReducerEnum, EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ZeroAddress } from 'ethers'
import { PoolOffchainDataCTO, PoolOffchainDataSchemaValues } from './PoolOffchainDataCTO'

export const PoolBaseAccountCTOSchemaValues = {
  version: yup
    .string()
    .label('Data Version')
    .required()
    .default('5.0.0')
    .meta(
      toFieldMetaData({ propName: 'version', description: 'Data Version ', stringType: StringTypeEnum.DATA_VERSION })
    ),
  id: yup
    .string()
    .label('PoolId poolAddr-lenderAddr')
    .required()
    .default('NOT_SET')
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of poolAccount ', stringType: StringTypeEnum.ID })),
  blockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .default(0)
    .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block number of the data' })),
  timestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  timestampDateString: yup
    .string()
    .required()
    .label('Timestamp Date')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'timestampDateString',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
        description: 'Timestamp Date of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME,
      })
    ),
  timestampString: yup
    .string()
    .required()
    .default(' ')
    .label('Timestamp Date Time')
    .meta(
      toFieldMetaData({
        propName: 'timestampString',
        description: 'Timestamp of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
      })
    ),
  dayNumber: yup
    .number()
    .label('Day Number')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'dayNumber',
        description: 'Day number of timestamp',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
      })
    ),
  timeOfDay: yup
    .number()
    .label('Time of Day')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'timeOfDay',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_TIME_OF_DAY,
      })
    ),

  poolType: yup
    .number<PoolTypeEnum>()
    .oneOf(Object.values(PoolTypeEnum).filter((v) => typeof v === 'number') as number[])
    .label('Pool Type')
    .defined()
    .default(PoolTypeEnum.NotPool)
    .meta(
      toFieldMetaData({
        propName: 'poolType',
        description: 'Type 0 is a term pool, type 1 is a flexible pool, type 2 is a dynamic pool.',
        stateToString: PoolTypeToString,
      })
    ),

  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',

        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  vaultName: yup
    .string()
    .required()
    .label('Vault Name')
    .default(' ')
    .meta(
      toFieldMetaData({
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
        propName: 'vaultName',
        description: 'Name of the Loan Vault',
      })
    ),
  liquidityAssetAddr: yupEthAddrRequired
    .label('Liquidity Asset Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'liquidityAssetAddr',
        description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  accountAddr: yupEthAddrRequired
    .label('Account Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'accountAddr',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
        description: 'The white-listed blockchain wallet address associated with this account. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  tokenBalance: yupErc20WeiRequired
    .label('Vault Token Balance')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'tokenBalance',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
        description: 'The number of vault tokens held by the account. Debits and credits add/subtract from this value.',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  principalEarningInterest: yupErc20WeiRequired
    .label('Principal Earning Interest')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'principalEarningInterest',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description:
          'The current amount of loan principal earning the interestRate for this account. Debits and credits add/subtract from this value. Fees paid does not subtract from this value.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  liquidityTokenSymbol: yup
    .string()
    .required()
    .label('Symbol for Liquidity Token')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',
        description: 'Symbol for the Liquidity Token',
        example: 'USDC',
      })
    ),
  currencyLabel: yup
    .string()
    .required()
    .default(' ')
    .label('Label for Liquidity Token')
    .meta(
      toFieldMetaData({
        propName: 'currencyLabel',
        description: 'Label for the Liquidity Token',
        example: 'USDC',
      })
    ),
  rewardsAccruedInDay: yupErc20WeiOptional.label('Rewards Accrued in Day').meta(
    toFieldMetaData({
      propName: 'rewardsAccruedInDay',
      description: 'Rewards Accrued in Day',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.Disabled,
    })
  ),
  rewardsAccrued: yupErc20WeiOptional.label('Rewards Accrued').meta(
    toFieldMetaData({
      propName: 'rewardsAccrued',
      description: 'Rewards Accrued',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.Disabled,
    })
  ),
  rewardsDistributed: yupErc20WeiOptional.label('Rewards Distributed').meta(
    toFieldMetaData({
      propName: 'rewardsDistributed',
      description: 'Rewards Distributed',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.Disabled,
    })
  ),
  /* OFFCHAIN DATA */

  ...PoolOffchainDataSchemaValues,
}

export const PoolBaseAccountSummaryCTOSchemaValues = {
  startBlockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'startBlockNumber',
        description: 'Block Number',
        map: 'blockNumber',
        reducer: CTOReducerEnum.FIRST,
      })
    ),
  startTimestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'startTimestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
        map: 'timestamp',
        reducer: CTOReducerEnum.FIRST,
      })
    ),
  startDayNumber: yup
    .number()
    .label('Start Day Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'startDayNumber',
        description: 'startDayNumber',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
        map: 'dayNumber',
        reducer: CTOReducerEnum.FIRST,
      })
    ),
  endDayNumber: yup
    .number()
    .label('End Day Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'endDayNumber',
        description: 'endDayNumber',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
        map: 'dayNumber',
        reducer: CTOReducerEnum.LAST,
      })
    ),
  endBlockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'endBlockNumber',
        description: 'Block Number',
        map: 'blockNumber',
        reducer: CTOReducerEnum.LAST,
      })
    ),
  endTimestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'endTimestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
        map: 'timestamp',
        reducer: CTOReducerEnum.LAST,
      })
    ),

  averageTokenBalance: yupErc20WeiRequired.label('AverageVault Token Balance').meta(
    toFieldMetaData({
      propName: 'averageTokenBalance',
      description: 'The number of vault tokens held by the account.',
      stringType: StringTypeEnum.PPT_SHARES,
      map: 'tokenBalance',
      reducer: CTOReducerEnum.AVERAGE,
    })
  ),

  sumInterestInADay: yupErc20WeiRequired.label('Sum of daily interest').meta(
    toFieldMetaData({
      propName: 'sumInterestInADay',
      description: 'Sum of daily interest.',
      stringType: StringTypeEnum.STABLE_COINS,
      map: 'interestInDay',
      reducer: CTOReducerEnum.SUM,
    })
  ),
  averagePrincipalEarningInterest: yupErc20WeiRequired.label('Average Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'averagePrincipalEarningInterest',
      description: 'Average Principal Earning Interest',
      stringType: StringTypeEnum.STABLE_COINS,
      map: 'principalEarningInterest',
      reducer: CTOReducerEnum.SUM,
    })
  ),
}
const PoolBaseOverviewCTOSchemaInfer = yup.object(PoolBaseAccountCTOSchemaValues)
export const PoolBaseAccountCTOSchema: yup.ObjectSchema<PoolBaseAccountCTO> = PoolBaseOverviewCTOSchemaInfer
export interface PoolBaseAccountCTO extends PoolOffchainDataCTO {
  id: string
  version: string
  poolType: PoolTypeEnum
  poolAddr: EthAddr
  vaultName: string
  accountAddr: EthAddr
  tokenBalance: ERC20Wei
  liquidityAssetAddr: EthAddr
  principalEarningInterest: ERC20Wei
  timestamp: number
  timestampDateString: string
  timestampString: string
  timeOfDay: number
  dayNumber: number
  blockNumber: number
  liquidityTokenSymbol: string
  currencyLabel: string
  rewardsAccruedInDay?: ERC20Wei
  rewardsAccrued?: ERC20Wei
  rewardsDistributed?: ERC20Wei
}

type PoolBaseAccountCTOInfer = InferType<typeof PoolBaseOverviewCTOSchemaInfer>

export const _testAPoolBaseAccount: PoolBaseAccountCTO = {} as unknown as PoolBaseAccountCTOInfer
export const _testBPoolBaseAccount: PoolBaseAccountCTOInfer = {} as unknown as PoolBaseAccountCTO
